<template>
  <div class="main-list-collections critical-component-clear container">
    <h1
      :id="`section-title-${liquid.sectionId}`"
      class="main-list-collections__title"
    >
      {{ liquid.collectionsListTitle }}
    </h1>

    <div
      ref="collectionGrid"
      class="main-list-collections__grid grid"
    >
      <div
        v-for="collection in filteredCollections"
        :key="`collection-card-${collection.id}`"
        class="col xs2 l4"
      >
        <a
          class="main-list-collections__card"
          :href="collection.url"
        >
          <responsive-image
            :alt="collection.alt"
            :image="collection.image"
            :ratio="'3:2'"
            block-class="main-list-collections"
            lazyload
          />
          <h2 class="body-1">
            {{ collection.title }}
          </h2>
        </a>
      </div>
      <div ref="loadingPlaceholder" />
    </div>
  </div>
</template>
<script>

/**
 * Vue: Main list collections (main-list-collections)
 * -----------------------------------------------------------------------------
 * List collections page section.
 * @param {String} liquid.sectionId - Section ID
 * @param {Object[]} liquid.collections - Initial list of collections
 * @param {String} liquid.collections.id - ID of the collection
 * @param {String} liquid.collections.title - Title of the collection
 * @param {String} liquid.collections.url - URL of the collection
 * @param {Object} liquid.collections.image - Image object of the collection
 * @param {String} liquid.collections.alt - Alternative text for the collection image
 * @param {String} liquid.collectionsListTitle - Translated list title
 * @param {Number} liquid.collectionsPerPage - Collections per page
 *
 */

import collections from '~/graphql/queries/collections.gql'
import { formatImage } from '~/helpers/format'

const HIDE_FROM_LIST_METAFIELD = 'hide_from_collections_list'

export default {
  name: 'MainListCollections',
  props: {
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
        collections: [],
        collectionsListTitle: '',
        collectionsPerPage: 10,
      }),
    },
  },
  data() {
    return {
      intersectionObserver: null,
      loading: false,
      lastCursorLoaded: null,
      hasNextPage: true,
      collections: this.liquid.collections,
    }
  },
  computed: {

    /**
     * Returns the filtered collections by the hide prop
     */
    filteredCollections() {
      return this.collections.filter(c => !c.hide)
    },
  },

  /**
   * On mounted we set up the intersection observer for the infinite scroll
   */
  mounted() {
    this.intersectionObserver = new IntersectionObserver((entries) => {
      if (entries[0].intersectionRatio > 0 && !this.loading && this.hasNextPage) {
        this.queryCollections()
      }
    })
    this.intersectionObserver.observe(this.$refs.loadingPlaceholder)
  },
  beforeUnmount() {
    this.intersectionObserver.disconnect()
  },
  methods: {

    /**
     * Gets the next collections from the storefront API
     */
    async queryCollections() {
      try {
        this.loading = true
        const response = await cnvs.Query({
          locale: cnvs.store.language,
          query: collections,
          variables: {
            first: this.liquid.collectionsPerPage,
            after: this.lastCursorLoaded,
            metafieldsIdentifiers: [{ namespace: 'custom', key: HIDE_FROM_LIST_METAFIELD }],
          },
        })
        this.collections = this.collections.concat(response.collections.nodes.map(c => {
          const image = c.image || c.products.nodes[0]?.featuredImage
          return {
            id: c.id,
            title: c.title,
            url: c.onlineStoreUrl,
            image: formatImage(image),
            alt: image.altText,
            hide: c.metafields.find(meta => meta?.key === HIDE_FROM_LIST_METAFIELD)?.value,
          }
        }))

        this.lastCursorLoaded = response.collections.pageInfo.endCursor
        this.hasNextPage = response.collections.pageInfo.hasNextPage
      } catch (error) {
        console.error(error)
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style lang="scss">
@import './main-list-collections';
</style>
